import { Chip, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useRegion } from '../auth/RegionContext';
import type { AccessibleRegion } from '../auth/permissions';

/**
 * Top-bar region control (step 0.5).
 *
 * Interactive ONLY for users who can access more than one market (GLOBAL):
 * the decision is `accessibleRegions.length > 1`. Single-region users get a
 * static, non-interactive label (a Chip) of their region — the interactive
 * control is never rendered for them, so they genuinely cannot switch (this
 * matches the access model; it is not a hidden/disabled button).
 *
 * On change it calls `setActiveRegion` (per-session; resets on reload — see
 * RegionContext). The chosen region flows into the data provider's `region`
 * filter wherever a list passes it (e.g. the Stores list).
 */
export const RegionSwitcher = () => {
  const { accessibleRegions, activeRegion, setActiveRegion } = useRegion();

  if (accessibleRegions.length <= 1) {
    return (
      <Chip
        size="small"
        variant="outlined"
        label={activeRegion}
        aria-label={`Region ${activeRegion}`}
        sx={{ mr: 1, color: 'inherit', borderColor: 'currentColor' }}
      />
    );
  }

  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={activeRegion}
      aria-label="Active region"
      onChange={(_event, value: AccessibleRegion | null) => {
        // value is null when the active button is re-clicked — ignore.
        if (value) setActiveRegion(value);
      }}
      sx={{
        mr: 1,
        '& .MuiToggleButton-root': {
          color: 'inherit',
          borderColor: 'currentColor',
          px: 1.25,
          py: 0.25,
        },
      }}
    >
      {accessibleRegions.map((region) => (
        <ToggleButton key={region} value={region} aria-label={region}>
          {region}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};
